import { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { LogIn } from 'lucide-react';
import { api } from '../api/client';
import { ErrorBox, Loading, useBrand, Wordmark } from '../components/ui';

async function devCall<T>(method: 'GET' | 'POST', url: string, code: string, body?: unknown): Promise<T> {
  const res = await fetch(url, {
    method, credentials: 'same-origin',
    headers: { 'x-requested-with': 'emolice-mrp', 'x-access-code': code, ...(body ? { 'content-type': 'application/json' } : {}) },
    body: body ? JSON.stringify(body) : undefined,
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error((data as { error?: { message?: string } }).error?.message ?? `Sign-in failed (${res.status}).`);
  return data as T;
}

export type LoginText = { headline: string; line: string; footer: string; title: string; subtitle: string };
type AuthConfig = { mode: 'entra' | 'dev'; accessCodeRequired?: boolean; text?: LoginText };

export function LoginPage() {
  const cfg = useQuery({ queryKey: ['auth-config'], queryFn: () => api.get<AuthConfig>('/api/auth/config') });
  const t = cfg.data?.text;
  const brand = useBrand().data;
  useEffect(() => { if (brand) document.title = brand.appName; }, [brand]);
  const [codeInput, setCodeInput] = useState('');
  const [code, setCode] = useState<string | null>(null);
  const needCode = !!cfg.data?.accessCodeRequired;
  const users = useQuery({
    queryKey: ['dev-users', code], retry: false,
    queryFn: () => devCall<{ users: { id: string; displayName: string; email: string; role: string }[] }>('GET', '/api/auth/dev-users', code ?? ''),
    enabled: cfg.data?.mode === 'dev' && (!needCode || code !== null),
  });
  const qc = useQueryClient();
  const login = async (userId: string) => {
    await devCall('POST', '/api/auth/dev-login', code ?? '', { userId });
    qc.clear();
    window.location.assign('/');
  };
  return (
    <div className="login-wrap">
      <aside className="login-side">
        <Wordmark size="lg" light />
        <div>
          {t?.headline && <h1 data-testid="login-headline">{t.headline}</h1>}
          {t?.line && <p className="muted" style={{ marginTop: 12, maxWidth: 420 }}>{t.line}</p>}
        </div>
        <div className="muted" style={{ fontSize: 12 }}>{t?.footer || brand?.appName}</div>
      </aside>
      <main className="login-main">
      <div className="login-card">
        <div style={{ marginBottom: 20 }}><h2>{t?.title ?? 'Sign in'}</h2>{t?.subtitle && <div className="muted">{t.subtitle}</div>}</div>
        {cfg.isLoading && <Loading lines={2} />}
        {cfg.error && <ErrorBox error={cfg.error} />}
        {cfg.data?.mode === 'entra' && (
          <a className="btn primary" style={{ width: '100%', justifyContent: 'center', padding: 10 }} href={`/auth/login?returnTo=${encodeURIComponent(window.location.pathname)}`}><LogIn size={16} /> Sign in with Microsoft</a>
        )}
        {cfg.data?.mode === 'dev' && (
          <div className="col">
            <div className="alert warn">Test sign-in</div>
            {needCode && !users.data && (
              <form className="col" onSubmit={(e) => { e.preventDefault(); setCode(codeInput); }}>
                <label className="field">Access code<input className="input" type="password" autoComplete="current-password" autoFocus value={codeInput} onChange={(e) => setCodeInput(e.target.value)} data-testid="access-code" /></label>
                <button className="btn primary" type="submit" disabled={!codeInput}>Continue</button>
                {users.error && <div className="alert bad">{(users.error as Error).message}</div>}
              </form>
            )}
            {users.data?.users.map((u) => (
              <button key={u.id} className="btn" style={{ justifyContent: 'space-between' }} onClick={() => login(u.id)} data-testid={`login-${u.email}`}>
                <span>{u.displayName}</span><span className="badge">{u.role}</span>
              </button>
            ))}
            {users.data?.users.length === 0 && <div className="muted">No users yet. Run <span className="mono">npm run admin:create</span>.</div>}
          </div>
        )}
      </div>
      </main>
    </div>
  );
}
